/**
 * aiParsers.js
 * Best-effort structure extraction from the LLM's markdown answers.
 *
 * Since the RAG rewrite these modules only stream prose — there is no structured JSON to read
 * any more. The report still needs a short structured summary (which crops, what yield range),
 * so we recover it from the markdown.
 *
 * Every parser here is best-effort by design: returning an empty array or null is a NORMAL
 * outcome and must never break a caller. The prose remains the source of truth in the report.
 */

/** Remove <think> blocks and fenced code so parsers only see the visible answer. */
export function stripThink(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .replace(/<think>[\s\S]*$/i, '')      // unterminated block from a cut-off stream
    .replace(/```[\s\S]*?```/g, '')
    .trim();
}

/** Strip markdown emphasis, list markers and stray pipes from a candidate name. */
function cleanName(raw) {
  return String(raw || '')
    .replace(/\*\*|__|[*_`|]/g, '')
    .replace(/^\s*\d+[.)]\s*/, '')
    .replace(/^\s*[-+•]\s*/, '')
    .replace(/[:：].*$/, '')
    // Drop a trailing parenthetical that only carries the confidence, e.g. "Date Palm (88%)".
    .replace(/\s*\([^)]*\d[^)]*\)\s*$/, '')
    // ...and the unparenthesised shapes models actually emit: "Date Palm - 88%",
    // "Date Palm — 88% confidence", "Date Palm with 88% confidence", "87/100".
    // Without this the suffix either lands in the crop NAME (dash form) or pushes
    // the line past the 4-word guard in push(), dropping the crop entirely.
    .replace(/[\s\-–—,]*(?:with|at|around|approximately)?\s*\d{1,3}(?:\.\d+)?\s*(?:%|\/\s*100)\s*(?:confidence|suitability|match|ثقة)?\s*$/i, '')
    .trim();
}

/** Pull a confidence percentage out of a line, if one is present. */
function extractConfidence(line) {
  const patterns = [
    /\((\d{1,3}(?:\.\d+)?)\s*%\)/,      // (87%)
    /(\d{1,3}(?:\.\d+)?)\s*%/,          // 87 %
    /(\d{1,3}(?:\.\d+)?)\s*\/\s*100/,   // 87/100
  ];
  for (const re of patterns) {
    const m = line.match(re);
    if (m) {
      const n = Number(m[1]);
      if (!isNaN(n) && n >= 0 && n <= 100) return Number(n.toFixed(2));
    }
  }
  return 0;
}

/**
 * Recover a crop shortlist from the model's markdown.
 * Handles numbered lists, bulleted lists and table rows.
 * @returns {Array<{crop: string, confidence: number}>} at most 5, possibly empty
 */
export function parseCropRecommendations(markdown) {
  const text = stripThink(markdown);
  if (!text) return [];

  const out = [];
  const seen = new Set();
  const push = (name, confidence) => {
    const crop = cleanName(name);
    // Guard against picking up whole sentences or prose fragments.
    if (!crop || crop.length > 40 || crop.split(/\s+/).length > 4) return;
    if (/^(crop|recommendation|name|confidence|rank|suitability)$/i.test(crop)) return;
    const key = crop.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ crop, confidence });
  };

  for (const line of text.split('\n')) {
    if (out.length >= 5) break;
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Markdown table row: | Crop | 87% |
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim());
      if (cells.length >= 1 && !/^[-: ]+$/.test(cells[0])) {
        push(cells[0], extractConfidence(trimmed));
      }
      continue;
    }

    // Numbered or bulleted list item.
    if (/^\s*(\d+[.)]|[-+•])\s+/.test(trimmed)) {
      push(trimmed, extractConfidence(trimmed));
    }
  }

  return out.slice(0, 5);
}

/**
 * Recover a yield figure or range from the model's markdown.
 * @returns {{low: number, high: number, unit: string}|null}
 */
export function parseYieldRange(markdown) {
  const text = stripThink(markdown);
  if (!text) return null;

  // Matches "3,200" / "3 200" (grouped) and "3500" (plain run of digits).
  // A bare \d{1,3}(?:[,\s]\d{3})* fails on 4+ digit numbers with no separator.
  const num = '(?:\\d{1,3}(?:[,\\s]\\d{3})+|\\d+)(?:\\.\\d+)?';
  // Capturing, and hectare spellings come FIRST so the longer alternative wins.
  // The unit must be reported, not assumed: the caller converts acre -> hectare,
  // and hardcoding 'kg/acre' here inflated a kg/ha answer by 2.47x in the PDF.
  const unitWords = '(kg\\s*\\/?\\s*acre|kg\\s*\\/?\\s*hectare|kg\\s*\\/?\\s*ha|kg|كجم|كيلو)';
  const toNum = s => Number(String(s).replace(/[,\s]/g, ''));

  // 'kg' alone means the model did not state an area unit - say so rather than
  // guessing, and let the caller decide what to do about it.
  const normUnit = (raw) => {
    const u = String(raw || '').toLowerCase().replace(/\s+/g, '');
    if (u.includes('acre')) return 'kg/acre';
    // 'hectare' must be tested separately: it does NOT contain the substring
    // 'ha' (it reads h-e-c-t-a-r-e), so an includes('ha') check alone misses it
    // and the value gets multiplied by 2.47 as though it were acres.
    if (u.includes('hectare') || u.includes('ha')) return 'kg/ha';
    return 'kg';
  };

  // A range: "3,200 - 3,800 kg/acre" / "3200 to 3800 kg" / "3200 – 3800 كجم"
  const rangeRe = new RegExp(`(${num})\\s*(?:-|–|—|to|إلى)\\s*(${num})\\s*${unitWords}`, 'i');
  const range = text.match(rangeRe);
  if (range) {
    const low = toNum(range[1]);
    const high = toNum(range[2]);
    if (!isNaN(low) && !isNaN(high)) {
      return { low: Math.min(low, high), high: Math.max(low, high), unit: normUnit(range[3]) };
    }
  }

  // A single scalar: "approximately 3,500 kg/acre"
  const scalarRe = new RegExp(`(${num})\\s*${unitWords}`, 'i');
  const scalar = text.match(scalarRe);
  if (scalar) {
    const v = toNum(scalar[1]);
    if (!isNaN(v)) return { low: v, high: v, unit: normUnit(scalar[2]) };
  }

  return null;
}
